import axios from "axios";
import {
	AUTH_API_BASE,
	getStoredLoginRealm,
	setStoredLoginRealm,
} from "@/services/authConfig";

/**
 * Auth Service
 * Manages access token in memory. Refresh token is stored as an HttpOnly
 * cookie by auth-api and sent automatically via withCredentials.
 */

// JS-readable marker that "a session probably exists." We can't check the
// real refresh_token cookie because auth-api sets it HttpOnly. This flag is
// the SPA's belief about session presence, used only to avoid an empty
// /refresh round-trip on first/unauthenticated visits. The server is still
// authoritative — the flag is cleared whenever /refresh actually fails.
const HAS_SESSION_KEY = "auth_has_session";

let accessToken = "";

// Deduplicate concurrent refresh calls
let refreshPromise = null;

const authClient = axios.create({
	baseURL: AUTH_API_BASE,
	headers: { "Content-Type": "application/json" },
	withCredentials: true,
	timeout: 10000,
});

export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
	accessToken = token || "";
	if (accessToken) {
		// Tag that a session exists so future bootstraps know to call /refresh.
		localStorage.setItem(HAS_SESSION_KEY, "1");
	}
};

export const clearAccessToken = () => {
	accessToken = "";
};

export const getAuthHeader = () => {
	return accessToken ? `Bearer ${accessToken}` : null;
};

/**
 * Clear auth state and redirect to login.
 */
export const forceLogout = () => {
	clearAccessToken();
	document.cookie = "refresh_token=; path=/; max-age=0; samesite=lax";
	document.cookie = "auth_realm=; path=/; max-age=0; samesite=lax";
	// Preserve the remembered login realm across the storage wipe so a returning
	// user on a non-subdomain host skips the email prompt next time.
	const rememberedRealm = getStoredLoginRealm();
	localStorage.clear();
	sessionStorage.clear();
	if (rememberedRealm) {
		setStoredLoginRealm(rememberedRealm.realm, rememberedRealm.email);
	}
	window.location.href = "/login";
};

/**
 * Refresh the access token via auth-api.
 * Returns the new access token or throws on failure.
 *
 * Skips the network call when no session marker is present — saves an empty
 * 400 round-trip on first visits and on /login. The marker is set whenever
 * setAccessToken stores a token (login or successful refresh) and cleared
 * whenever /refresh fails or the user logs out.
 */
export const refreshAccessToken = async () => {
	if (localStorage.getItem(HAS_SESSION_KEY) !== "1") {
		throw new Error("No session");
	}

	if (refreshPromise) return refreshPromise;

	refreshPromise = authClient
		.post("/refresh")
		.then((res) => {
			const token = res.data?.access_token;
			if (!token) throw new Error("No access_token in refresh response");
			setAccessToken(token);
			return token;
		})
		.catch((err) => {
			// Clear the in-memory token and session marker on refresh failure.
			// Don't call forceLogout() here — let the caller decide (router
			// guard redirects to login without a full page reload).
			clearAccessToken();
			localStorage.removeItem(HAS_SESSION_KEY);
			throw err;
		})
		.finally(() => {
			refreshPromise = null;
		});

	return refreshPromise;
};

/**
 * Call auth-api logout to revoke refresh token in Redis and clear cookies.
 */
export const logout = async () => {
	try {
		const headers = accessToken
			? { Authorization: `Bearer ${accessToken}` }
			: {};
		await authClient.post("/logout", {}, { headers });
	} catch (e) {
		console.warn("Auth API logout failed (non-fatal):", e.message);
	}
};

/**
 * Get user info from auth-api.
 */
export const getUserInfo = async () => {
	const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
	const res = await authClient.get("/userinfo", { headers });
	return res.data;
};

export default {
	getAccessToken,
	setAccessToken,
	clearAccessToken,
	refreshAccessToken,
	getAuthHeader,
	logout,
	getUserInfo,
	forceLogout,
};
